import React, { useState, useEffect } from 'react';
import { useResendTimer } from '../hooks/useResendTimer';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { api, API } from '../lib/api';
import { CheckCircle2, ShieldCheck, Smartphone, KeyRound, UserPlus, Mail, Building2 } from 'lucide-react';

const TG_CITIES = ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam', 'Ramagundam',
  'Mahbubnagar', 'Nalgonda', 'Adilabad', 'Suryapet', 'Siddipet', 'Sangareddy', 'Other'];

const CustomerRegister = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    name: '', mobile: '', email: '', district: '', city: '', company: '',
    customer_type: 'Individual', otp: '',
  });
  const update = (k, v) => setForm({ ...form, [k]: v });
  const { remaining: resendIn, start: startResend } = useResendTimer(30);

  useEffect(() => {
    api.getDistricts().then(setDistricts).catch(() => setDistricts([]));
    api.getCities().then(setCities).catch(() => setCities([]));
  }, []);

  // Cascading city list: when a district is picked, show only cities linked to it;
  // fall back to the full list (or the hardcoded set) so the form is never empty.
  const cityOptions = (() => {
    const all = cities.length ? cities.map((c) => c.name) : TG_CITIES;
    if (!form.district) return all;
    const filtered = cities.filter((c) => c.district === form.district).map((c) => c.name);
    return filtered.length ? filtered : all;
  })();

  const sendOtp = async () => {
    if (!form.name.trim() || !form.mobile || form.mobile.length < 10) {
      toast({ title: 'Enter your name and 10-digit mobile number', variant: 'destructive' });
      return;
    }
    if (!form.city) {
      toast({ title: 'Please select your city', variant: 'destructive' });
      return;
    }
    try {
      setSubmitting(true);
      const res = await api.sendOtp(form.mobile);
      setStep(2);
      startResend();
      if (res.dev_otp) {
        setDevOtp(res.dev_otp);
        toast({ title: 'OTP sent (dev mode)', description: `Dev OTP: ${res.dev_otp}` });
      } else {
        toast({ title: 'OTP sent', description: `Sent to +91 ${form.mobile}` });
      }
    } catch (err) {
      toast({ title: 'Failed to send OTP', description: err?.response?.data?.detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const register = async (e) => {
    e.preventDefault();
    if (!form.otp || form.otp.length < 4) {
      toast({ title: 'Enter the OTP', variant: 'destructive' });
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/customers/register`, {
        name: form.name,
        mobile: form.mobile,
        otp: form.otp,
        email: form.email,
        city: form.city,
        district: form.district,
        company: form.company,
        customer_type: form.customer_type,
      });
      // Cookie set by backend; cache profile only.
      localStorage.setItem('tgmart_user', JSON.stringify(res.data.user));
      toast({ title: 'Welcome to TelanganaMart!', description: 'Your account has been created.' });
      setStep(3);
    } catch (err) {
      toast({ title: 'Registration failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="bg-gradient-to-br from-[#2c54b2] to-[#1e3a8a] text-white">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="inline-block px-3 py-1 bg-white/10 backdrop-blur rounded-full text-xs font-medium mb-4 border border-white/20">For Buyers</div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Create your TelanganaMart Buyer Account</h1>
          <p className="text-blue-100">Get instant access to verified vendors, post requirements, and receive the best quotes.</p>
        </div>
      </section>

      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          {step === 1 && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#ee5e23]" /> Buyer Registration</h2>
              <p className="text-sm text-gray-600 mb-5">Fill in your details. We'll verify your mobile with an OTP.</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Full Name *</Label>
                  <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your full name" />
                </div>
                <div>
                  <Label className="text-xs">Mobile Number *</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="flex items-center px-3 border rounded-md bg-gray-50 text-sm text-gray-700">+91</div>
                    <Input value={form.mobile} onChange={(e) => update('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">District</Label>
                    <Select value={form.district} onValueChange={(v) => setForm({ ...form, district: v, city: '' })}>
                      <SelectTrigger data-testid="customer-district-trigger"><SelectValue placeholder="Select district" /></SelectTrigger>
                      <SelectContent className="max-h-60">{districts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">City *</Label>
                    <Select value={form.city} onValueChange={(v) => update('city', v)}>
                      <SelectTrigger data-testid="customer-city-trigger"><SelectValue placeholder="Select city" /></SelectTrigger>
                      <SelectContent className="max-h-60">{cityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">I am buying as</Label>
                  <Select value={form.customer_type} onValueChange={(v) => update('customer_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Individual">Individual</SelectItem>
                      <SelectItem value="Business">Business</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.customer_type === 'Business' && (
                  <div>
                    <Label className="text-xs">Company / Business Name</Label>
                    <Input value={form.company} onChange={(e) => update('company', e.target.value)} placeholder="Your company name" />
                  </div>
                )}
                <Button onClick={sendOtp} disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11 mt-2">
                  <Smartphone className="w-4 h-4 mr-2" /> {submitting ? 'Sending OTP...' : 'Send OTP & Continue'}
                </Button>
                <div className="text-xs text-center text-gray-500">By proceeding, you agree to our Terms of Use & Privacy Policy.</div>
              </div>
            </>
          )}

          {step === 2 && (
            <form onSubmit={register}>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Verify your Mobile</h2>
              <p className="text-sm text-gray-600 mb-5">Enter the OTP sent to +91 {form.mobile}.</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Enter OTP</Label>
                  <Input value={form.otp} onChange={(e) => update('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center tracking-widest text-lg" placeholder="4-digit OTP" />
                </div>
                {devOtp && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    Dev mode: OTP is <b className="font-mono">{devOtp}</b>
                  </div>
                )}
                <Button type="submit" disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11">
                  <KeyRound className="w-4 h-4 mr-2" /> {submitting ? 'Verifying...' : 'Verify & Create Account'}
                </Button>
                <div className="flex items-center justify-between pt-1">
                  <button type="button" onClick={() => setStep(1)} className="text-xs text-[#2c54b2] hover:underline">Change details</button>
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={submitting || resendIn > 0}
                    data-testid="customer-resend-otp"
                    className="text-xs text-[#2c54b2] hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                  >
                    {resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {step === 3 && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Welcome, {form.name}!</h2>
              <p className="text-sm text-gray-600 mb-6">Your buyer account is ready.</p>
              <Button onClick={() => navigate('/')} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white">Start Exploring</Button>
            </div>
          )}
        </div>

        {step !== 3 && (
          <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-gray-600">
            <div className="flex items-center gap-1 justify-center"><ShieldCheck className="w-3.5 h-3.5 text-green-600" /> Secure OTP</div>
            <div className="flex items-center gap-1 justify-center"><Mail className="w-3.5 h-3.5 text-green-600" /> No spam</div>
            <div className="flex items-center gap-1 justify-center"><Building2 className="w-3.5 h-3.5 text-green-600" /> Free forever</div>
          </div>
        )}

        <p className="text-xs text-gray-500 text-center mt-4">
          Want to sell on TelanganaMart? <Link to="/vendor/register" className="text-[#2c54b2] hover:underline">Register as Vendor</Link>
        </p>
      </div>
      <Footer />
    </div>
  );
};

export default CustomerRegister;
